import { MeetingTemplateList } from '@wundo/ui';
import type {
  MeetingTemplateKind,
  MeetingTemplateRecord,
} from '@wundo/shared/meetingTemplate.ts';
import { meetingTemplateRow } from '@/lib/meetingTemplateRows.ts';
import {
  MEETING_TEMPLATE_KIND_TEXT,
  MEETING_TEMPLATE_KINDS,
} from '@/lib/meetingTemplateKinds.ts';
import { reorderWithinKind } from '@wundo/shared/reorderWithinKind.ts';

export interface MeetingTemplateKindListsProps {
  templates: MeetingTemplateRecord[];
  /** Ready to show, above the lists. */
  error?: string;
  onOpen: (templateId: string) => void;
  onCreate: (kind: MeetingTemplateKind) => void;
  /** The whole list, every kind, in the order it now stands in. */
  onReorder: (ids: string[]) => void;
  /** Left out, no row offers to become its kind's default. */
  onMakeDefault?: (templateId: string) => void;
}

/**
 * One list of conversation types, shown as a card per kind of meeting. A type
 * serves one kind and each kind has its own default, so the marker and the
 * make-default action name the kind they apply to.
 */
export function MeetingTemplateKindLists({
  templates,
  error,
  onOpen,
  onCreate,
  onReorder,
  onMakeDefault,
}: MeetingTemplateKindListsProps) {
  return (
    <>
      {error && (
        <p role='alert' className='text-sm text-danger'>
          {error}
        </p>
      )}

      {MEETING_TEMPLATE_KINDS.map((kind) => {
        const text = MEETING_TEMPLATE_KIND_TEXT[kind];
        return (
          <MeetingTemplateList
            key={kind}
            labels={{
              newTemplate: text.newTemplate,
              defaultMarker: text.defaultMarker,
              makeDefault: text.makeDefault,
            }}
            onOpen={onOpen}
            onMakeDefault={onMakeDefault}
            sections={[
              {
                key: kind,
                title: text.section,
                emptyMessage: text.emptyMessage,
                runs: [
                  {
                    key: kind,
                    templates: templates
                      .filter((template) => template.kind === kind)
                      .map(meetingTemplateRow),
                    canReorder: true,
                    onReorder: (ids) =>
                      onReorder(reorderWithinKind(templates, kind, ids)),
                  },
                ],
                canCreate: true,
                canMakeDefault: onMakeDefault !== undefined,
                onCreate: () => onCreate(kind),
              },
            ]}
          />
        );
      })}
    </>
  );
}
